import {styled} from '@linaria/react';
import React, {ComponentProps, FC, ReactNode} from 'react';
import {COLORS} from '../../../constants/colors';
import {FONT_SIZES} from '../../../constants/typography';
import type {ImageType} from '../../../types/Image';
import {Icon} from '../../atoms/Icon/Icon';
import {Text} from '../../atoms/Text/Text';
import {ProductImage} from '../../molecules/ProductImage/ProductImage';
import {CountBadge} from './CountBadge';
import {CountBadgeProps} from './types';

type ThumbnailProps = {
  image?: ImageType;
  count?: CountBadgeProps;
  icon?: ComponentProps<typeof Icon>;
};

interface CategoryTileProps {
  label: ReactNode;
  thumbnail?: ThumbnailProps;
  iconComponent?: ReactNode;
  labelSize?: keyof typeof FONT_SIZES;
  labelColor?: keyof typeof COLORS | 'inherit';
  backgroundColor?: string;
  className?: string;
}

export const CategoryTile: FC<CategoryTileProps> = ({
  label,
  thumbnail,
  iconComponent,
  labelSize = 'px16',
  labelColor,
  backgroundColor,
  className,
}) => {
  return (
    <Wrapper className={className} $backgroundColor={backgroundColor}>
      {iconComponent}
      <Label size={labelSize} weight="mediumHeavy" color={labelColor}>
        {label}
      </Label>
      {thumbnail && (
        <ThumbnailContainer>
          <Thumbnail thumbnail={thumbnail} />
        </ThumbnailContainer>
      )}
    </Wrapper>
  );
};

const Thumbnail: FC<{thumbnail: ThumbnailProps}> = ({thumbnail}) => {
  const {count, image, icon} = thumbnail;

  if (icon) {
    return <Icon {...icon} />;
  }

  if (count) {
    return <CountBadge itemCount={count.itemCount} theme={count.theme} />;
  }

  if (image) {
    return (
      <ProductImage
        src={image.url}
        padded={image.padded}
        alt={image.description}
      />
    );
  }

  return null;
};

const Wrapper = styled.div<{
  $backgroundColor?: string;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
  overflow: hidden;
  padding: 12px 16px;
  position: relative;
  background: ${({$backgroundColor}) =>
    $backgroundColor || COLORS.ProductImageBackground};
  border-radius: 8px;
  height: 100%;
  min-height: 66px;
`;

const Label = styled(Text)`
  align-items: center;
  display: flex;
  overflow-wrap: anywhere;
  hyphens: auto;
  text-overflow: ellipsis;
  flex-grow: 1;
`;

const ThumbnailContainer = styled.div`
  box-sizing: border-box;
  flex-shrink: 0;
  min-width: 30px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: start;
  justify-content: center;
`;
